import {
    MAP_TYPES,
    TEAM_FORMATS,
    createRef,
    updateRef,
    uploadMapImage,
    deleteMapImage,
    type GameMap,
    type MapType,
    type TeamFormat,
} from '../api/game-refs';
import { apiRequest } from '../api';
import { state } from '../state';
import { t } from '../i18n';
import { attachAutocomplete } from './autocomplete';
import { getMapAuthors, loadMapAuthors } from './map-authors';
import { teamFormatLabel } from './map-presets';

// ============================================================
// Общая модалка карты: добавление и правка.
// Используется и в разделе «Карты», и в форме игры (кнопки «+»/«✎» у карты).
// ============================================================

/** Минимум полей карты, нужный для открытия формы правки. */
export interface MapEditable {
    id: number;
    name: string;
    alt_name: string | null;
    map_types: MapType[];
    /** Форматы командной карты (только при типе team). */
    team_formats?: TeamFormat[];
    player_count: number | null;
    author: string | null;
    link: string | null;
    /** Пометка «дисбалансная карта». */
    unbalanced?: boolean;
    image_url: string | null;
}

let initialized = false;

let modal: HTMLElement | null = null;
let form: HTMLFormElement | null = null;
let titleEl: HTMLElement | null = null;
let nameInput: HTMLInputElement | null = null;
let altInput: HTMLInputElement | null = null;
let playersInput: HTMLInputElement | null = null;
let authorInput: HTMLInputElement | null = null;
let linkInput: HTMLInputElement | null = null;
let unbalancedInput: HTMLInputElement | null = null;
let typesBox: HTMLElement | null = null;
/** Формат командной карты: обёртка (скрывается без типа «Командная») и чекбоксы. */
let formatsField: HTMLElement | null = null;
let formatsBox: HTMLElement | null = null;
let imageBox: HTMLElement | null = null;
let deleteBtn: HTMLButtonElement | null = null;

let editingId: number | null = null;
let pendingImageFile: File | null = null;
let pendingImageRemove = false;
/** Картинка карты на сервере (при правке) — по ссылке её не заменяем. */
let currentImageUrl: string | null = null;
let linkStatus: HTMLElement | null = null;
/** Ссылка, по которой уже заполняли форму, и номер запроса (ответ после закрытия окна не нужен). */
let linkLoaded = '';
let linkRequest = 0;

/** Заполнение формы новой карты (например, из реплея). */
export interface MapPrefill {
    name: string;
    alt_name: string | null;
    player_count: number | null;
    author: string | null;
    link: string | null;
    image: File | null;
}

/** Промис открытой модалки: резолвится сохранённой картой или null при отмене. */
let resolveFn: ((g: GameMap | null) => void) | null = null;

let previewEl: HTMLElement | null = null;

/** Кэширует ссылки и вешает обработчики. Безопасно вызывать многократно. */
export function initMapModal(): void {
    if (initialized) return;
    initialized = true;

    modal = document.getElementById('map-modal');
    form = document.getElementById('map-form') as HTMLFormElement | null;
    titleEl = document.getElementById('map-modal-title');
    nameInput = document.getElementById('map-field-name') as HTMLInputElement | null;
    altInput = document.getElementById('map-field-alt') as HTMLInputElement | null;
    playersInput = document.getElementById('map-field-players') as HTMLInputElement | null;
    authorInput = document.getElementById('map-field-author') as HTMLInputElement | null;
    linkInput = document.getElementById('map-field-link') as HTMLInputElement | null;
    linkStatus = document.getElementById('map-link-status');
    // Вставленная ссылка на карту Battle.net заполняет пустые поля и картинку
    linkInput?.addEventListener('paste', () => setTimeout(() => void fillFromLink(), 0));
    linkInput?.addEventListener('change', () => void fillFromLink());
    unbalancedInput = document.getElementById('map-field-unbalanced') as HTMLInputElement | null;
    // Автоподстановка по уже существующим авторам карт
    if (authorInput) attachAutocomplete(authorInput, getMapAuthors);
    void loadMapAuthors();
    typesBox = document.getElementById('map-field-types');
    formatsField = document.getElementById('map-formats-field');
    formatsBox = document.getElementById('map-field-formats');
    // Тип «Командная» показывает/скрывает выбор формата
    typesBox?.addEventListener('change', renderFormatsVisibility);
    imageBox = document.getElementById('map-field-image');

    document.getElementById('map-modal-close')?.addEventListener('click', () => closeModal(null));
    document.getElementById('btn-cancel-map')?.addEventListener('click', () => closeModal(null));
    deleteBtn = document.getElementById('btn-delete-map') as HTMLButtonElement | null;
    deleteBtn?.addEventListener('click', () => void deleteMap());
    form?.addEventListener('submit', (e) => void submit(e));
    // Escape закрывает модалку. Фаза перехвата + stopImmediatePropagation,
    // чтобы заодно не закрылась модалка игры, если она открыта под ней.
    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        if (!modal || modal.classList.contains('hidden')) return;
        e.stopImmediatePropagation();
        e.preventDefault();
        closeModal(null);
    }, true);
}

/** Открывает форму добавления карты (пустую или заполненную `prefill`). */
export function openAddMapModal(prefill?: MapPrefill): Promise<GameMap | null> {
    initMapModal();
    if (!modal) return Promise.resolve(null);
    settlePending(null);

    hidePreview();
    editingId = null;
    deleteBtn?.classList.add('hidden');
    pendingImageFile = prefill?.image ?? null;
    pendingImageRemove = false;
    currentImageUrl = null;
    resetLinkStatus(prefill?.link ?? '');
    if (titleEl) titleEl.textContent = t('maps.form_title');
    if (nameInput) nameInput.value = prefill?.name ?? '';
    if (altInput) altInput.value = prefill?.alt_name ?? '';
    if (playersInput) playersInput.value = prefill?.player_count != null ? String(prefill.player_count) : '';
    if (authorInput) authorInput.value = prefill?.author ?? '';
    if (linkInput) linkInput.value = prefill?.link ?? '';
    if (unbalancedInput) unbalancedInput.checked = false;
    renderTypeChecks([], []);
    renderImageControl(null);
    void loadMapAuthors();
    modal.classList.remove('hidden');
    nameInput?.focus();
    return new Promise((res) => { resolveFn = res; });
}

/** Открывает форму правки карты. */
export function openEditMapModal(map: MapEditable): Promise<GameMap | null> {
    initMapModal();
    if (!modal) return Promise.resolve(null);
    settlePending(null);

    hidePreview();
    editingId = map.id;
    deleteBtn?.classList.remove('hidden');
    pendingImageFile = null;
    pendingImageRemove = false;
    currentImageUrl = map.image_url;
    resetLinkStatus(map.link ?? '');
    if (titleEl) titleEl.textContent = t('maps.form_title_edit');
    if (nameInput) nameInput.value = map.name;
    if (altInput) altInput.value = map.alt_name ?? '';
    if (playersInput) playersInput.value = map.player_count != null ? String(map.player_count) : '';
    if (authorInput) authorInput.value = map.author ?? '';
    if (linkInput) linkInput.value = map.link ?? '';
    if (unbalancedInput) unbalancedInput.checked = Boolean(map.unbalanced);
    renderTypeChecks(map.map_types ?? [], map.team_formats ?? []);
    renderImageControl(map.image_url);
    void loadMapAuthors();
    modal.classList.remove('hidden');
    nameInput?.focus();
    return new Promise((res) => { resolveFn = res; });
}

function settlePending(result: GameMap | null): void {
    const r = resolveFn;
    resolveFn = null;
    r?.(result);
}

function closeModal(result: GameMap | null): void {
    modal?.classList.add('hidden');
    form?.reset();
    editingId = null;
    pendingImageFile = null;
    pendingImageRemove = false;
    linkRequest++;
    hidePreview();
    settlePending(result);
}

/**
 * Мягкое удаление карты (игры сохраняют ссылку, админ может вернуть).
 * Промис модалки резолвится картой с `deleted_at` — вызывающий убирает её из списков.
 */
async function deleteMap(): Promise<void> {
    if (editingId === null) return;
    const name = nameInput?.value.trim() || '';
    if (!confirm(t('maps.delete_confirm', { name }))) return;
    if (deleteBtn) deleteBtn.disabled = true;
    try {
        const res = await apiRequest<{ deleted: GameMap }>(`/api/game-refs/maps/${editingId}`, {
            method: 'DELETE',
            token: state.token,
        });
        closeModal(res.deleted);
    } catch (err) {
        alert(t('maps.delete_error') + (err instanceof Error ? err.message : String(err)));
    } finally {
        if (deleteBtn) deleteBtn.disabled = false;
    }
}

function resetLinkStatus(link: string): void {
    linkLoaded = link;
    linkRequest++;
    linkStatus?.classList.add('hidden');
}

function showLinkStatus(key: 'maps.link_loading' | 'maps.link_filled' | 'maps.link_not_found'): void {
    if (!linkStatus) return;
    linkStatus.textContent = t(key);
    linkStatus.classList.remove('hidden');
}

/** Данные карты по ссылке Battle.net (сервер берёт их из каталога Аркады): только пустые поля. */
async function fillFromLink(): Promise<void> {
    const link = linkInput?.value.trim() ?? '';
    if (link === linkLoaded) return;
    linkLoaded = link;
    if (!/(battlenet:+\/+starcraft\/map|sc2arcade\.com\/map)\/\d+\/\d+/i.test(link)) {
        linkStatus?.classList.add('hidden');
        return;
    }
    const request = ++linkRequest;
    showLinkStatus('maps.link_loading');
    let data: {
        map: { name: string; altName: string | null; playerCount: number | null; author: string | null; link: string };
        png: string | null;
    };
    try {
        data = await apiRequest(`/api/games/map-from-link?link=${encodeURIComponent(link)}`, { token: state.token });
    } catch {
        if (request === linkRequest) showLinkStatus('maps.link_not_found');
        return;
    }
    if (request !== linkRequest) return;

    const fill = (input: HTMLInputElement | null, value: string | number | null) => {
        if (input && !input.value.trim() && value != null) input.value = String(value);
    };
    fill(nameInput, data.map.name);
    fill(altInput, data.map.altName);
    fill(playersInput, data.map.playerCount);
    fill(authorInput, data.map.author);
    // Ссылку приводим к виду battlenet:://… (так хранятся остальные)
    if (linkInput) linkInput.value = linkLoaded = data.map.link;
    const hasImage = pendingImageFile || (currentImageUrl && !pendingImageRemove);
    if (data.png && !hasImage) {
        const bytes = Uint8Array.from(atob(data.png), (c) => c.charCodeAt(0));
        pendingImageFile = new File([bytes], 'map.png', { type: 'image/png' });
        pendingImageRemove = false;
        renderImageControl(currentImageUrl);
    }
    showLinkStatus('maps.link_filled');
}

/** Галочки типов карты (обычная / командная / КБ). */
function renderTypeChecks(selected: MapType[], formats: TeamFormat[]): void {
    if (!typesBox) return;
    typesBox.innerHTML = '';
    for (const type of MAP_TYPES) {
        const label = document.createElement('label');
        label.className = 'admin-ref-type';
        const cb = document.createElement('input');
        cb.type = 'checkbox';
        cb.value = type;
        cb.checked = selected.includes(type);
        const span = document.createElement('span');
        span.textContent = t(`map_type.${type}` as any);
        label.append(cb, span);
        typesBox.appendChild(label);
    }
    renderFormatChecks(formats);
}

/** Галочки форматов командной карты (2на2, 3на3, 4на4, 2на2на2, 2на2на2на2). */
function renderFormatChecks(selected: TeamFormat[]): void {
    if (!formatsBox) return;
    formatsBox.innerHTML = '';
    for (const format of TEAM_FORMATS) {
        const label = document.createElement('label');
        label.className = 'admin-ref-type';
        const cb = document.createElement('input');
        cb.type = 'checkbox';
        cb.value = format;
        cb.checked = selected.includes(format);
        const span = document.createElement('span');
        span.textContent = teamFormatLabel(format);
        label.append(cb, span);
        formatsBox.appendChild(label);
    }
    renderFormatsVisibility();
}

/** Блок форматов виден только у карты с типом «Командная». */
function renderFormatsVisibility(): void {
    formatsField?.classList.toggle('hidden', !selectedTypes().includes('team'));
}

function selectedTypes(): MapType[] {
    if (!typesBox) return [];
    return [...typesBox.querySelectorAll<HTMLInputElement>('input[type=checkbox]:checked')]
        .map((cb) => cb.value as MapType);
}

function selectedFormats(): TeamFormat[] {
    if (!formatsBox) return [];
    return [...formatsBox.querySelectorAll<HTMLInputElement>('input[type=checkbox]:checked')]
        .map((cb) => cb.value as TeamFormat);
}

/** Миниатюра картинки: клик — выбрать файл, ✕ — убрать. Файл грузится при сохранении. */
function renderImageControl(currentUrl: string | null): void {
    if (!imageBox) return;
    imageBox.innerHTML = '';

    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.hidden = true;
    fileInput.addEventListener('change', () => {
        const file = fileInput.files?.[0];
        fileInput.value = '';
        if (!file) return;
        pendingImageFile = file;
        pendingImageRemove = false;
        renderImageControl(currentUrl);
    });

    const previewUrl = pendingImageFile
        ? URL.createObjectURL(pendingImageFile)
        : (pendingImageRemove ? null : currentUrl);
    const hasImage = Boolean(previewUrl);

    const thumb = document.createElement('button');
    thumb.type = 'button';
    thumb.className = 'admin-map-thumb' + (hasImage ? '' : ' admin-map-thumb--empty');
    thumb.title = hasImage ? t('admin.map_image_hover') : t('admin.map_image_upload');
    if (hasImage) {
        const img = document.createElement('img');
        img.src = previewUrl!;
        img.alt = '';
        thumb.appendChild(img);
        thumb.addEventListener('mouseenter', () => showPreview(previewUrl!, thumb));
        thumb.addEventListener('mouseleave', hidePreview);
    } else {
        thumb.textContent = '🖼';
    }
    thumb.addEventListener('click', () => fileInput.click());
    imageBox.appendChild(thumb);

    if (hasImage) {
        const rm = document.createElement('button');
        rm.type = 'button';
        rm.className = 'admin-map-img-remove';
        rm.title = t('admin.map_image_remove');
        rm.textContent = '✕';
        rm.addEventListener('click', () => {
            if (pendingImageFile) pendingImageFile = null;
            else pendingImageRemove = true;
            hidePreview();
            renderImageControl(currentUrl);
        });
        imageBox.appendChild(rm);
    }

    imageBox.appendChild(fileInput);
}

async function submit(e: Event): Promise<void> {
    e.preventDefault();
    if (!nameInput) return;

    const name = nameInput.value.trim();
    if (!name) {
        alert(t('maps.name_required'));
        nameInput.focus();
        return;
    }

    const playersRaw = playersInput?.value.trim() ?? '';
    const types = selectedTypes();
    const payload: Record<string, unknown> = {
        name,
        alt_name: altInput?.value.trim() || null,
        map_types: types,
        // Форматы актуальны только у командной карты
        team_formats: types.includes('team') ? selectedFormats() : [],
        player_count: playersRaw === '' ? null : Number(playersRaw),
        author: authorInput?.value.trim() || null,
        link: linkInput?.value.trim() || null,
        unbalanced: Boolean(unbalancedInput?.checked),
    };

    const wasNew = editingId === null;
    const saveBtn = document.getElementById('btn-save-map') as HTMLButtonElement | null;
    if (saveBtn) { saveBtn.disabled = true; saveBtn.textContent = t('common.saving'); }

    try {
        let saved: GameMap;
        if (wasNew) {
            saved = await createRef<GameMap>('maps', payload, state.token);
            if (pendingImageFile) saved = await uploadMapImage(saved.id, pendingImageFile, state.token);
        } else {
            saved = await updateRef<GameMap>('maps', editingId!, payload, state.token);
            if (pendingImageFile) saved = await uploadMapImage(editingId!, pendingImageFile, state.token);
            else if (pendingImageRemove) saved = await deleteMapImage(editingId!, state.token);
        }
        closeModal(saved);
    } catch (err) {
        alert((wasNew ? t('maps.new_error') : t('maps.save_error')) +
            (err instanceof Error ? err.message : String(err)));
    } finally {
        if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = t('common.save'); }
    }
}

// --- Крупный предпросмотр картинки при наведении ---
function hidePreview(): void {
    previewEl?.remove();
    previewEl = null;
}

function showPreview(src: string, anchor: HTMLElement): void {
    hidePreview();
    const box = document.createElement('div');
    box.className = 'map-preview';
    const img = document.createElement('img');
    img.src = src;
    img.alt = '';
    box.appendChild(img);
    document.body.appendChild(box);

    const r = anchor.getBoundingClientRect();
    const b = box.getBoundingClientRect();
    const pad = 8;

    let left = r.right + 14;
    if (left + b.width > window.innerWidth - pad) left = window.innerWidth - b.width - pad;
    if (left < pad) left = pad;

    let top = r.top;
    if (top + b.height > window.innerHeight - pad) top = window.innerHeight - b.height - pad;
    if (top < pad) top = pad;

    box.style.left = `${left}px`;
    box.style.top = `${top}px`;
    previewEl = box;
}
